import { Component, inject } from '@angular/core';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { NgbNav, NgbNavItem, NgbNavLink } from '@ng-bootstrap/ng-bootstrap';
import { UserRole } from '../../../../enums/user-role';
import { AuthService } from '@services/auth.service';
import { LogoComponent } from '../../logo/logo.component';
import { ProfileCabinetComponent } from '../profile-cabinet/profile-cabinet.component';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  MatButtonToggle,
  MatButtonToggleChange,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { LocaleService } from '@services/local.service';
import { environment } from '../../../../../environments/environment';
import { HasRoleDirective } from '../../../../directives/has-role.directive';

interface AppLink {
  id: number;
  label: string;
  routerLink: string;
  isActive?: boolean;
  roles: UserRole[];
}

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    NgbNav,
    NgbNavItem,
    RouterLink,
    NgbNavLink,
    AsyncPipe,
    RouterLinkActive,
    ProfileCabinetComponent,
    LogoComponent,
    TranslateModule,
    MatButtonToggle,
    MatButtonToggleGroup,
    HasRoleDirective,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent {
  readonly route = inject(ActivatedRoute);
  private localeService = inject(LocaleService);
  readonly allRoles = Object.values(UserRole);

  active = 1;
  links: AppLink[] = [
    {
      id: 2,
      label: 'front.link.header.dashboard',
      routerLink: '/dashboard',
      roles: [UserRole.LOCATING_OPERATOR, UserRole.UNIT_ADMINISTRATOR],
    },
    {
      id: 6,
      label: 'front.link.header.roles-and-permissions',
      routerLink: '/roles-and-permissions',
      roles: [UserRole.PORTAL_ADMINISTRATOR],
    },
    {
      id: 14,
      label: 'front.link.header.staff-scheduler',
      routerLink: '/staff-scheduler',
      roles: [UserRole.SECURITY_MANAGER],
    },
    {
      id: 13,
      label: 'front.link.header.floor-plan',
      routerLink: '/floor-plan',
      roles: [UserRole.SECURITY_COMMAND_CENTRE],
    },
    ...(!environment.isWlmhEnabled
      ? [
          {
            id: 3,
            label: 'front.link.header.manage-assignments',
            routerLink: '/assignments',
            roles: [UserRole.UNIT_ADMINISTRATOR],
          },
          {
            id: 4,
            label: 'front.link.header.manage-code-teams',
            routerLink: '/code-teams',
            roles: [UserRole.LOCATING_OPERATOR],
          },
          {
            id: 7,
            label: 'front.link.header.mobile-app',
            routerLink: '/mobile-app-configuration',
            roles: [UserRole.PORTAL_ADMINISTRATOR],
          },
          {
            id: 8,
            label: 'front.link.header.web-portal-configuration',
            routerLink: '/web-portal-configuration',
            roles: [UserRole.PORTAL_ADMINISTRATOR],
          },
          {
            id: 10,
            label: 'front.link.header.mobile-locations',
            routerLink: '/mobile-locations',
            roles: [UserRole.PORTAL_ADMINISTRATOR],
          },
        ]
      : []),
    ...(environment.isWlmhEnabled
      ? [
          {
            id: 12,
            label: 'front.link.header.reporting',
            routerLink: '/reports',
            roles: this.allRoles,
          },
        ]
      : []),

    {
      id: 9,
      label: 'front.link.header.global-configuration',
      routerLink: '/global-configuration',
      roles: [UserRole.PORTAL_ADMINISTRATOR],
    },

    {
      id: 11,
      label: 'front.link.header.qr-code',
      routerLink: '/qr-code',
      roles: [UserRole.UNIT_ADMINISTRATOR],
    },
  ];
  private authService = inject(AuthService);
  private router = inject(Router);

  handleLogOut() {
    this.authService.logOut();
    this.router.navigateByUrl('/login');
  }

  updateLocale(toggleChange: MatButtonToggleChange) {
    this.localeService.setLocale(toggleChange.value);
  }

  protected readonly environment = environment;
}
